import { mulberry32 } from '../game/rng';
import type { EdgeSpec, PieceEdges } from '../game/types';

/**
 * 拼图块的贝塞尔耳朵路径生成。
 *
 * 设计要点（保证 tab/blank 互补严丝合缝）：
 *  1. 耳朵中心固定在**边中点**（c=0.5），不做随机偏移 —— 这样无论从边的哪一端
 *     绘制（互补两边绘制方向相反），耳朵在共享边线上的位置一致。
 *  2. 耳朵轮廓**关于边中点左右对称**（颈左/颈右、头左/头右镜像）。
 *  3. tab 与 blank（同 seed）在"沿边 a / 外法线 b"局部坐标下关于 a 轴镜像（b 互反）。
 *  由 1+2+3，一块的 tab 凸起正好填满互补块的 blank 凹槽。
 *
 * 几何全部用纯函数 `edgePrims` / `piecePrisms` 表达（返回图元序列），便于在不依赖
 * Canvas 的 node 环境下做单元测试；`buildPiecePath2D` 才用到 DOM 的 Path2D。
 */

export interface Vec2 {
  x: number;
  y: number;
}

/** 一条边被分解成的图元。各图元首尾相接。 */
export type Prim =
  | { kind: 'line'; from: Vec2; to: Vec2 }
  | { kind: 'bezier'; from: Vec2; c1: Vec2; c2: Vec2; to: Vec2 };

// 耳朵几何系数（均相对边长 L；左右关于边中点对称）。
const NECK_HALF = 0.1; // 颈半宽
const HEAD_HALF = 0.2; // 头半宽
const HOOK = 0.04; // 颈部内凹钩深度（沿边朝中心）
const NECK_RISE = 0.15; // 颈部起始外抬比例
const SHOULDER = 0.85; // 头肩高度比例
const CROWN = 1.12; // 头顶隆起比例
/** 耳朵高度区间（相对边长）；上限也是最大外凸量，用于计算缓存 padding。 */
export const EAR_MIN = 0.2;
export const EAR_MAX = 0.26;

/** 由 seed 派生耳朵高度（同 seed → 同高度，且对称 → tab/blank 兼容）。 */
function earHeight(seed: number): number {
  const t = mulberry32(seed >>> 0)();
  return EAR_MIN + (EAR_MAX - EAR_MIN) * t;
}

function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}
function scale(v: Vec2, s: number): Vec2 {
  return { x: v.x * s, y: v.y * s };
}

/**
 * 一条边分解成的图元序列。
 * @param edge 边规格
 * @param from 边起点
 * @param u    沿边方向的单位向量（from→to）
 * @param n    外法线单位向量（朝主体外）
 * @param L    边长
 */
export function edgePrims(edge: EdgeSpec, from: Vec2, u: Vec2, n: Vec2, L: number): Prim[] {
  const to = add(from, scale(u, L));
  if (edge.type === 'flat') {
    return [{ kind: 'line', from, to }];
  }

  const dir = edge.type === 'tab' ? 1 : -1; // +1 朝外（+n），-1 朝内
  const eh = earHeight(edge.seed);
  const c = 0.5; // 耳朵中心固定在边中点

  // 局部坐标 (a 沿边系数, b 外法线系数) → 全局坐标
  const P = (a: number, b: number): Vec2 =>
    add(add(from, scale(u, a * L)), scale(n, b * L));

  const neckL = c - NECK_HALF;
  const neckR = c + NECK_HALF;
  const headL = c - HEAD_HALF;
  const headR = c + HEAD_HALF;

  const prims: Prim[] = [];

  // 1) 沿边到颈左
  prims.push({ kind: 'line', from, to: P(neckL, 0) });
  // 2) 颈左 → 头左（内凹钩 + 鼓起，形成蘑菇头颈部）
  prims.push({
    kind: 'bezier',
    from: P(neckL, 0),
    c1: P(neckL - HOOK, dir * eh * NECK_RISE),
    c2: P(headL + 0.02, dir * eh * SHOULDER),
    to: P(headL, dir * eh),
  });
  // 3) 头左 → 头右（头顶弧）
  prims.push({
    kind: 'bezier',
    from: P(headL, dir * eh),
    c1: P(headL + 0.02, dir * eh * CROWN),
    c2: P(headR - 0.02, dir * eh * CROWN),
    to: P(headR, dir * eh),
  });
  // 4) 头右 → 颈右（对称收回）
  prims.push({
    kind: 'bezier',
    from: P(headR, dir * eh),
    c1: P(headR - 0.02, dir * eh * SHOULDER),
    c2: P(neckR + HOOK, dir * eh * NECK_RISE),
    to: P(neckR, 0),
  });
  // 5) 颈右 → 边终点
  prims.push({ kind: 'line', from: P(neckR, 0), to: P(1, 0) });

  return prims;
}

/** 顺时针外法线：把 u 旋转 -90°（top 边 u=(1,0) → n=(0,-1) 朝上=主体外）。 */
function outwardNormal(u: Vec2): Vec2 {
  return { x: u.y, y: -u.x };
}

/** 整块四条边拼接成的图元序列（顺时针：上→右→下→左，闭合）。 */
export function piecePrisms(edges: PieceEdges, w: number, h: number): Prim[] {
  const tl: Vec2 = { x: 0, y: 0 };
  const tr: Vec2 = { x: w, y: 0 };
  const br: Vec2 = { x: w, y: h };
  const bl: Vec2 = { x: 0, y: h };

  const sides: { edge: EdgeSpec; from: Vec2; to: Vec2 }[] = [
    { edge: edges.top, from: tl, to: tr },
    { edge: edges.right, from: tr, to: br },
    { edge: edges.bottom, from: br, to: bl },
    { edge: edges.left, from: bl, to: tl },
  ];

  const prims: Prim[] = [];
  for (const s of sides) {
    const dx = s.to.x - s.from.x;
    const dy = s.to.y - s.from.y;
    const L = Math.hypot(dx, dy);
    const u: Vec2 = { x: dx / L, y: dy / L };
    const n = outwardNormal(u);
    prims.push(...edgePrims(s.edge, s.from, u, n, L));
  }
  return prims;
}

/** 构建可在 Canvas 上绘制/裁剪的 Path2D（依赖 DOM，仅浏览器环境调用）。 */
export function buildPiecePath2D(edges: PieceEdges, w: number, h: number): Path2D {
  const path = new Path2D();
  const prims = piecePrisms(edges, w, h);
  const first = prims[0]?.from;
  if (!first) return path;
  path.moveTo(first.x, first.y);
  for (const p of prims) {
    if (p.kind === 'line') {
      path.lineTo(p.to.x, p.to.y);
    } else {
      path.bezierCurveTo(p.c1.x, p.c1.y, p.c2.x, p.c2.y, p.to.x, p.to.y);
    }
  }
  path.closePath();
  return path;
}
